import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Spin } from 'antd';

import styled from 'styled-components';
import { CareerIcon, SkillIcon } from 'atoms';
import { SkillDemandLine } from 'molecules/SkillDemandLine';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { useSkillMap } from 'core';
import { skillMapHref } from 'router/careerLinks';

/**
 * Карта навыков (ТЗ 0039, шаг 5).
 *
 * Отвечает по порядку: что у меня есть → чего не хватает до трека → КАКОЙ КУРС
 * это даёт. Третий ответ и есть продающая петля; без него карта превращается в
 * список претензий к студенту.
 *
 * ⚠️ Востребованность подписана «в нашей ленте», а не «на рынке», и рядом
 * стоит дата снимка. Иначе экран выдаёт нашу же доску вакансий за рынок труда:
 * доска, набитая питоновскими вакансиями, по построению объявит Python самым
 * востребованным навыком.
 */
/** Сколько недостающих навыков показывать как «следующие». */
const NEXT_SKILLS = 3;

export const GrowthSkillMap = memo(() => {
  const { t, i18n } = useTranslation();
  const { skillMap, loading } = useSkillMap();

  if (loading && !skillMap) {
    return (
      <Center>
        <Spin />
      </Center>
    );
  }
  if (!skillMap) return null;

  const stamp = skillMap.demandComputedAt
    ? new Date(skillMap.demandComputedAt).toLocaleDateString(i18n.language)
    : null;

  /**
   * ТЗ 0069: недостающее разбито на «следующие три» и «позже».
   *
   * Аудит владельца: плотный каталог дефицитов не говорит, за что браться —
   * одиннадцать карточек подряд читаются как список претензий. Три — это
   * столько, сколько человек удерживает без выбора.
   *
   * 🔴 Наверх поднимаются навыки с `upcoming` — те, что откроются на курсе,
   * куда студент УЖЕ ходит. Они и есть ближайшие по факту, а не по порядку
   * справочника; предлагать вместо них купить новый курс значит продавать то,
   * что уже оплачено.
   */
  const missingSorted = [...skillMap.missing].sort(
    (a, b) => Number(Boolean(b.upcoming)) - Number(Boolean(a.upcoming)),
  );
  const nextSkills = missingSorted.slice(0, NEXT_SKILLS);
  const laterSkills = missingSorted.slice(NEXT_SKILLS);

  /** Карточка недостающего навыка. Одна функция на обе группы: две копии
   * разъехались бы на первой же правке. */
  const renderMissing = (row: typeof skillMap.missing[number]) => (
    <SkillCard key={row.id} verified={false} missing>
      <CardHead>
        <NameLink to={skillMapHref({ skill: row.slug })}>
          <SkillIcon
            slug={row.slug}
            name={row.name}
            category={row.category}
            iconUrl={row.iconUrl}
            size={22}
          />
          <SkillName>{row.name}</SkillName>
        </NameLink>
      </CardHead>
      <SkillDemandLine demand={row.demand} />
      {/* ТЗ 0040 доводка: если навык даёт курс, на который студент
          УЖЕ ходит, честный ответ — «откроется на занятии N», а не
          «купите курс». Иначе карта предлагала купить то, что
          человек уже оплатил. */}
      {row.upcoming ? (
        <Upcoming>
          {t('growth.map.upcoming', {
            n: row.upcoming.lessonNumber,
            left: row.upcoming.lessonsLeft,
          })}
        </Upcoming>
      ) : null}
      {row.providers.length ? (
        <Providers>
          <ProvidersLabel>{t('growth.map.whereToGet')}</ProvidersLabel>
          {row.providers.map((provider) => (
            <Provider key={provider.courseId}>
              {/* 🔴 Ссылка наружу, на сайт школы: витрины курсов в
                  ЛМС нет вовсе, и без неё экран говорил «не хватает
                  Kubernetes, его даёт курс DevOps» — и всё, идти
                  было некуда. Ровно здесь продающая петля и рвалась. */}
              <CourseLink href={provider.courseUrl} target="_blank" rel="noopener noreferrer">
                {provider.courseName}
              </CourseLink>
              {provider.completed ? (
                <ProviderNote> {t('growth.map.courseDone')}</ProviderNote>
              ) : null}
            </Provider>
          ))}
        </Providers>
      ) : (
        <Meta>{t('growth.map.noCourseYet')}</Meta>
      )}
    </SkillCard>
  );

  return (
    <>
      <Block>
        <BlockHead>
          <BlockTitle>{t('growth.map.mine')}</BlockTitle>
          <Count>{t('growth.map.mineCount', { n: skillMap.mine.length })}</Count>
        </BlockHead>

        {skillMap.mine.length ? (
          <Grid>
            {skillMap.mine.map((row) => (
              <SkillCard key={row.id} verified={row.source === 'course'}>
                <CardHead>
                  <NameLink to={skillMapHref({ skill: row.slug })}>
                    <SkillIcon
                      slug={row.slug}
                      name={row.name}
                      category={row.category}
                      iconUrl={row.iconUrl}
                      size={22}
                    />
                    <SkillName>{row.name}</SkillName>
                  </NameLink>
                  {row.source === 'course' ? <Verified>{t('growth.map.verified')}</Verified> : null}
                </CardHead>
                <Level>{t('growth.map.level', { n: row.level })}</Level>
                {row.grantedCourseName ? (
                  <Meta>
                    {row.grantedLessonNumber
                      ? t('growth.map.fromLesson', {
                          n: row.grantedLessonNumber,
                          course: row.grantedCourseName,
                        })
                      : t('growth.map.fromCourse', { course: row.grantedCourseName })}
                  </Meta>
                ) : null}
                <SkillDemandLine demand={row.demand} />
                {row.isHidden ? <Meta>{t('growth.map.hiddenInCv')}</Meta> : null}
              </SkillCard>
            ))}
          </Grid>
        ) : (
          <Muted>{t('growth.map.noSkills')}</Muted>
        )}
      </Block>

      <Block>
        <BlockHead>
          <BlockTitle>
            {skillMap.trackTitle
              ? t('growth.map.nextForTrack', { track: skillMap.trackTitle })
              : t('growth.map.nextTitle')}
          </BlockTitle>
        </BlockHead>

        {missingSorted.length ? (
          <>
            <Grid>{nextSkills.map(renderMissing)}</Grid>

            {/* «Позже» свёрнуто: одиннадцать карточек подряд читаются как
                список претензий, а не как план. Раскрыть можно — спрятать
                насовсем нельзя, это те же цели трека. */}
            {laterSkills.length ? (
              <Later>
                <LaterSummary>{t('growth.map.laterTitle', { n: laterSkills.length })}</LaterSummary>
                <Grid>{laterSkills.map(renderMissing)}</Grid>
              </Later>
            ) : null}
          </>
        ) : (
          <Muted>{t('growth.map.nothingMissing')}</Muted>
        )}
      </Block>

      {skillMap.nextCourses.length ? (
        <Block>
          <BlockHead>
            <BlockTitle>{t('growth.next.title')}</BlockTitle>
          </BlockHead>
          <Hint>{t('growth.next.hint')}</Hint>
          <Grid>
            {skillMap.nextCourses.map((course) => (
              <NextCard key={course.courseId}>
                {/* Знак стоит СНАРУЖИ подписи, как у навыков выше: `SkillName`
                    делят три места, и менять его раскладку ради одного —
                    трогать два чужих. */}
                <CourseHead>
                  <CareerIcon name="learning" size={16} />
                  <SkillName>{course.courseName}</SkillName>
                </CourseHead>
                {/* Объяснение «зачем» — из навыков, которые курс закроет.
                    Рекомендация без причины читается как реклама. */}
                <Meta>
                  {t('growth.next.covers', {
                    n: course.covered,
                    total: course.totalMissing,
                  })}
                </Meta>
                <Chips>
                  {course.adds.map((name) => (
                    <Chip key={name}>{name}</Chip>
                  ))}
                </Chips>
                <NextLink href={course.courseUrl} target="_blank" rel="noopener noreferrer">
                  {t('growth.next.action')}
                </NextLink>
              </NextCard>
            ))}
          </Grid>
        </Block>
      ) : null}

      {stamp ? <Footnote>{t('growth.map.demandNote', { date: stamp })}</Footnote> : null}
    </>
  );
});

const Block = styled.section`
  margin-bottom: 26px;
`;

const BlockHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const BlockTitle = styled.h2`
  margin: 0;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Count = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const SkillCard = styled.div<{ verified: boolean; missing?: boolean }>`
  padding: 12px 14px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid
    ${({ verified, missing }) =>
      verified ? Colors.signal_green : missing ? Colors.main : Colors.gray};
  border-style: ${({ missing }) => (missing ? 'dashed' : 'solid')};
`;

/**
 * Кнопкой сделана ТОЛЬКО шапка с названием, а не карточка целиком.
 *
 * 🔴 Иначе внутри кнопки оказывалась ссылка на курс — и клик по ней открывал
 * и вкладку с курсом, и шторку навыка разом. Ломалась именно та половина
 * карточки, ради которой она и существует: «где это взять». Плюс кнопка с
 * интерактивным содержимым — невалидный HTML, а читалка склеивала всю
 * простыню текста в одно имя.
 *
 * ТЗ 0039, доводка UX: имя ведёт на ОТДЕЛЬНУЮ СТРАНИЦУ навыка, а не открывает
 * шторку. Владелец про шторку сказал: «открывается справа поп-ап — не
 * очевидно». У страницы есть адрес: её можно дать ссылкой, открыть в новой
 * вкладке и вернуться назад браузером, чего шторка не умела в принципе.
 */
const NameLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  color: ${Colors.black};
  /* Подчёркивание ПОСТОЯННОЕ, а не по наведению. Прямо под этими карточками
     ссылки на курсы подчёркнуты, а имя навыка — нет, и на экране получалось,
     что кликабельно только то, что подчёркнуто. Владелец про раздел сказал:
     «непонятно, как этим пользоваться» — вот один из ответов.
     Цвет линии приглушён, чтобы 12 карточек не превратились в частокол. */
  text-decoration: underline;
  text-decoration-color: ${Colors.gray};
  text-underline-offset: 3px;

  &:hover {
    color: ${Colors.black};
    text-decoration-color: ${Colors.black};
  }
`;

const CardHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const SkillName = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const CourseHead = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Verified = styled.span`
  /* signal_green (#37AB34) — цвет РАМКИ и значка успеха: как текст на белом
     это 2.98:1 при норме 4.5. Тёмно-зелёный marker_green даёт 7.36 в
     светлой теме и 6.93 в тёмной (правило в context/constants/colors.ts). */
  color: ${Colors.marker_green};
  white-space: nowrap;
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Level = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Meta = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Providers = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 4px;
`;

const ProvidersLabel = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Provider = styled.span`
  ${fontService.text({ type: TextType.extraSmall })}
`;

const CourseLink = styled.a`
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  text-decoration: underline;
`;

const ProviderNote = styled.span`
  color: ${Colors.gray_dark};
`;

const Muted = styled.p`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const NextCard = styled.div`
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid ${Colors.main};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  padding: 3px 9px;
  border-radius: 999px;
  background: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const NextLink = styled.a`
  align-self: flex-start;
  margin-top: auto;
  padding: 7px 16px;
  border-radius: 8px;
  background: ${Colors.main};
  color: ${Colors.btn_text};
  text-decoration: none;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Hint = styled.p`
  margin: 0 0 10px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Footnote = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const Upcoming = styled.div`
  margin-top: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: ${Colors.bg_gray};
  color: ${Colors.marker_green};
  ${fontService.text({ size: 12 })};
`;

const Later = styled.details`
  margin-top: 16px;
`;

const LaterSummary = styled.summary`
  cursor: pointer;
  margin-bottom: 12px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: 2px;
  }
`;
